<template>
  <div class="zoom" ref="container">
    <div
      class="zoom-body"
      @panzoomchange="handleZoomChange"
      ref="body"
    >
      <slot></slot>
      <resize-observer @notify="notify" />
    </div>
    <div class="zoom-control-group" v-if="!disabled">
      <div class="zoom-control">
        <v-btn color="grey darken-1" x-small depressed fab @click="zoom(getMinScale())">
          <v-icon>fullscreen_exit</v-icon>
        </v-btn>
      </div>
      <div class="zoom-control">
        <v-btn color="grey darken-1" x-small depressed fab @click="zoomIn">
          <v-icon>add</v-icon>
        </v-btn>
      </div>
      <div class="zoom-control">
        <v-btn color="grey darken-1" x-small depressed fab @click="zoomOut">
          <v-icon>remove</v-icon>
        </v-btn>
      </div>
    </div>
    <resize-observer @notify="notify" />
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import Panzoom from '@panzoom/panzoom';
import { ResizeObserver } from 'vue-resize';

export const ZoomProvider = {
  data() {
    return {
      zoomState: {
        x: 0,
        y: 0,
        scale: 1,
      },
    };
  },
  methods: {
    getZoom() {
      return this.zoomState;
    },
    setZoom(newZoomState) {
      this.zoomState = newZoomState;
    },
    resetZoom() {
      this.zoomState = {
        x: 0,
        y: 0,
        scale: 1,
      };
    },
  },
  provide() {
    return {
      getZoom: this.getZoom,
      setZoom: this.setZoom,
      resetZoom: this.resetZoom,
    };
  },
};

// ゼロ除算を防ぐ安全な除算
const safeDivide = (dividend, divisor, fallbackValue) => {
  if (dividend && divisor) {
    return dividend / divisor;
  }
  return fallbackValue;
};

const ZOOM_STEP = 0.5;
const HALF_RATIO = 0.5;

export default {
  name: 'Zoom',
  components: {
    ResizeObserver,
  },
  props: {
    scale: {
      type: Number,
      default: 1, // 1 ~ *
    },
    x: {
      type: Number,
      default: 0, // 0 ~ 1
    },
    y: {
      type: Number,
      default: 0, // 0 ~ 1
    },
    disabled: Boolean,
  },
  methods: {
    // 正規化座標（0-1）でビューの中心を移動
    centerOn(x, y) {
      if (!this.isContentReady()) return;

      const actualScale = this.scale * this.getMinScale();
      const {
        viewportWidth,
        viewportHeight,
        originalContentWidth,
        originalContentHeight,
      } = this.getViewportAndContentDimensions();

      const { scaledContentWidth, scaledContentHeight } = this.calculateScaledDimensions(
        originalContentWidth,
        originalContentHeight,
        actualScale,
      );

      const normalizedPanX = this.calculateNormalizedPanPosition(x, viewportWidth, scaledContentWidth);
      const normalizedPanY = this.calculateNormalizedPanPosition(y, viewportHeight, scaledContentHeight);

      const coords = this.convertToPanzoomCoordinates(normalizedPanX, normalizedPanY);
      this.panzoom.pan(coords.x, coords.y);
    },
    // パン操作可能な境界範囲を計算
    getBoundingRect(relativeScale = this.scale) {
      if (!this.isContentReady()) {
        return {
          left: 0,
          right: 0,
          width: 0,
          top: 0,
          bottom: 0,
          height: 0,
        };
      }

      const {
        viewportWidth,
        viewportHeight,
        contentWidth,
        contentHeight,
      } = this.getViewportAndContentDimensions();

      const actualScale = relativeScale * this.getMinScale();
      const { scaledContentWidth, scaledContentHeight } = this.calculateScaledDimensions(
        contentWidth,
        contentHeight,
        actualScale,
      );

      const scaleOffsetWidth = this.calculateScaleOffset(contentWidth, scaledContentWidth);
      const scaleOffsetHeight = this.calculateScaleOffset(contentHeight, scaledContentHeight);

      const { minPan: minPanX, maxPan: maxPanX } = this.calculateAxisPanRange(
        viewportWidth,
        scaledContentWidth,
        scaleOffsetWidth,
        actualScale,
      );

      const { minPan: minPanY, maxPan: maxPanY } = this.calculateAxisPanRange(
        viewportHeight,
        scaledContentHeight,
        scaleOffsetHeight,
        actualScale,
      );

      return {
        left: minPanX,
        right: maxPanX,
        width: Math.abs(maxPanX - minPanX),
        top: minPanY,
        bottom: maxPanY,
        height: Math.abs(maxPanY - minPanY),
      };
    },
    // DOM要素のサイズを統一取得
    getViewportAndContentDimensions() {
      const { container, body: content } = this.$refs;

      return {
        // 表示枠のサイズ
        viewportWidth: container.clientWidth,
        viewportHeight: container.clientHeight,

        // コンテンツの元サイズ（centerOn用）
        originalContentWidth: container.scrollWidth,
        originalContentHeight: container.scrollHeight,

        // 実際のコンテンツ要素サイズ（他メソッド用）
        contentWidth: content.clientWidth,
        contentHeight: content.clientHeight,
      };
    },
    // コンテンツ準備状態をチェック
    isContentReady() {
      const { contentWidth, contentHeight } = this.getViewportAndContentDimensions();
      return contentHeight && contentWidth;
    },
    // スケール適用済みのコンテンツサイズを計算
    calculateScaledDimensions(contentWidth, contentHeight, scale) {
      return {
        scaledContentWidth: contentWidth * scale,
        scaledContentHeight: contentHeight * scale,
      };
    },
    // 中心基準スケーリングによる位置調整オフセットを計算
    calculateScaleOffset(originalSize, scaledSize) {
      // スケール適用により増加したサイズの半分を中心位置調整として返す
      return (scaledSize - originalSize) * HALF_RATIO;
    },
    // 軸ごとの正規化パン位置を計算（端固定・比例計算の統一処理）
    calculateNormalizedPanPosition(targetPosition, viewportSize, scaledContentSize) {
      const viewportHalf = viewportSize * HALF_RATIO;
      const targetCenterInContainer = scaledContentSize * targetPosition;

      // 端に近い場合は端に固定、中央は比例計算
      if (targetCenterInContainer < viewportHalf) {
        return 0;
      }
      if (targetCenterInContainer > (scaledContentSize - viewportHalf)) {
        return 1;
      }
      return (targetCenterInContainer - viewportHalf) / (scaledContentSize - viewportSize);
    },
    // 正規化座標をpanzoom座標系に変換
    convertToPanzoomCoordinates(normalizedPanX, normalizedPanY) {
      const { left: minPanX, width: panRangeWidth, top: minPanY, height: panRangeHeight } = this.getBoundingRect();

      return {
        x: (panRangeWidth * -normalizedPanX) + minPanX,
        y: (panRangeHeight * -normalizedPanY) + minPanY,
      };
    },
    // 軸ごとのパン可能範囲を計算（X/Y軸の重複ロジック統一）
    calculateAxisPanRange(viewportSize, scaledContentSize, scaleOffset, actualScale) {
      // eslint-disable-next-line no-mixed-operators
      const maxPan = (viewportSize - scaledContentSize + scaleOffset) / actualScale;
      const minPan = scaleOffset / actualScale;

      return { minPan, maxPan };
    },
    // 絶対スケールから相対スケールに変換
    convertToRelativeScale(absoluteScale) {
      const minimumScale = this.getMinScale();
      return absoluteScale / minimumScale;
    },
    // panzoom座標から正規化座標(0-1)に変換
    convertToNormalizedCoordinates(panX, panY, relativeScale) {
      const {
        left: minPanX, width: panRangeWidth, top: minPanY, height: panRangeHeight,
      } = this.getBoundingRect(relativeScale);

      const normalizedX = -safeDivide((panX - minPanX), panRangeWidth, 0);
      const normalizedY = -safeDivide((panY - minPanY), panRangeHeight, 0);

      return { x: normalizedX, y: normalizedY };
    },
    // コンテンツ全体がコンテナにフィットする最小スケール
    getMinScale() {
      if (!this.isContentReady()) return 1;

      const {
        viewportWidth,
        viewportHeight,
        contentWidth,
        contentHeight,
      } = this.getViewportAndContentDimensions();

      const heightFitRatio = safeDivide(viewportHeight, contentHeight, 0);
      const widthFitRatio = safeDivide(viewportWidth, contentWidth, 0);

      // より大きな比率を選択してコンテンツ全体をカバー
      return Math.max(widthFitRatio, heightFitRatio);
    },
    // プロップ値をpanzoom用の絶対座標に変換
    getPanzoomValues() {
      const absoluteScale = this.scale * this.getMinScale();
      const { left: minPanX, width: panRangeWidth, top: minPanY, height: panRangeHeight } = this.getBoundingRect();

      // 正規化座標(0-1)を絶対座標に変換
      const absolutePanX = (panRangeWidth * -this.x) + minPanX;
      const absolutePanY = (panRangeHeight * -this.y) + minPanY;

      return {
        x: absolutePanX,
        y: absolutePanY,
        scale: absoluteScale,
      };
    },
    handleZoomChange(event) {
      const { clientHeight: bodyHeight, clientWidth: bodyWidth } = this.$refs.body;
      if (bodyHeight && bodyWidth) {
        const { x, y, scale } = event.detail;
        const minScale = this.getMinScale();
        const rateScale = scale / minScale;
        this.$emit('update:scale', rateScale);

        const { left, width, top, height } = this.getBoundingRect(rateScale);
        const rateX = -safeDivide((x - left), width, 0);
        const rateY = -safeDivide((y - top), height, 0);
        this.$emit('update:x', rateX);
        this.$emit('update:y', rateY);
      }
    },
    // プロップ値に基づいてpanzoomの表示を刷新
    refresh() {
      if (!this.panzoom || !this.isContentReady()) return;

      // minScale設定を動的に更新
      const newMinScale = this.getMinScale();
      const { x: targetPanX, y: targetPanY, scale: targetScale } = this.getPanzoomValues();
      const adjustedScale = Math.max(targetScale, newMinScale);

      this.panzoom.setOptions({ silent: false });
      this.panzoom.zoom(adjustedScale, { silent: true });
      this.panzoom.pan(targetPanX, targetPanY, { contain: '' });
    },
    setDisabled(isDisabled = false) {
      this.panzoom.setOptions({
        disablePan: isDisabled,
        disableZoom: isDisabled,
        content: 'inside',
        cursor: isDisabled ? 'normal' : 'move',
        excludeClass: isDisabled ? '' : null,
      });
    },
    // コンテナ中心でズーム
    zoom(targetScale) {
      const {
        x: containerScreenX,
        y: containerScreenY,
        width: containerWidth,
        height: containerHeight,
      } = this.$refs.container.getBoundingClientRect();

      const centerPoint = {
        clientX: containerScreenX + (containerWidth * HALF_RATIO),
        clientY: containerScreenY + (containerHeight * HALF_RATIO),
      };

      this.panzoom.zoomToPoint(targetScale, centerPoint, { force: true });
    },
    zoomIn() {
      const currentScale = this.panzoom.getScale();
      this.zoom(currentScale + ZOOM_STEP);
    },
    zoomOut() {
      const currentScale = this.panzoom.getScale();
      this.zoom(currentScale - ZOOM_STEP);
    },
  },
  computed: {
    // eslint-disable-next-line no-underscore-dangle
    _zoomParams() {
      return {
        x: this.x,
        y: this.y,
        scale: this.scale,
      };
    },
  },
  created() {
    const REFRESH_INTERVAL = 100;
    this.notify = debounce(this.refresh, REFRESH_INTERVAL);
  },
  mounted() {
    this.panzoom = Panzoom(this.$refs.body, {
      step: 0.1,
      contain: 'outside',
      maxScale: Number.MAX_SAFE_INTEGER,
      silent: true,
    });
    this.$refs.container.addEventListener('wheel', this.panzoom.zoomWithWheel);
    this.setDisabled(this.disabled);
  },
  beforeDestroy() {
    this.$refs.container.removeEventListener('wheel', this.panzoom.zoomWithWheel);
    this.panzoom.destroy();
  },
  watch: {
    disabled(value) {
      this.setDisabled(value);
    },
    // eslint-disable-next-line no-underscore-dangle
    _zoomParams() {
      if (this.isContentReady()) {
        this.notify();
      }
    },
  },
};
</script>

<style scoped lang="sass">
.zoom
  height: 100%
  width: 100%
  position: relative
  font-size: 1rem

  &-body
    display: inline-block
    font-size: 0

  &-control
    button.v-btn
      color: white
      margin: 0.5em 0 0

    &-group
      position: absolute
      right: 0.5em
      bottom: 0.5em
</style>
